import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { Page, Quote as QuoteRenderer } from '@shinetools/invoicing-renderer';
import { Feature } from '@shinetools/pricing-plan-library';
import queryString from 'query-string';

import { QuoteStatus } from '__generated__/GQL';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Text from 'components/_core/Text';
import PdfPreview from 'components/DocumentPreview/PdfPreview';
import NavigationButton from 'components/NavigationButton';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import ContactPicker from 'features/Work/components/ContactPicker';
import { QUOTE_VALIDITY_DAYS_UNLIMITED } from 'features/Work/constants';
import PreviewLayout from 'features/Work/layout/Preview';
import { Client } from 'features/Work/pages/Clients/hooks/useGetClient';
import useSendDummyQuoteToUser from 'features/Work/pages/Quotes/hooks/mutations/useSendDummyQuoteToUser';
import { useQuoteModals } from 'features/Work/pages/Quotes/hooks/useQuoteModals';
import { getColorScheme } from 'features/Work/pages/utils';
import * as routes from 'features/Work/routes';

import { useClientContacts } from '../../../../../../hooks/useClientContacts';
import { computeQuoteSpecialMentions } from '../../../../../../utils/computeSpecialMentions';
import { InvoicingSpecialMentions } from '../../../../../hooks/useInvoicingSpecialMentionsQuery';
import { Quote } from '../../../../hooks/queries/useGetQuote';
import DetailHeader from './components/DetailHeader';
import RcProSidebar from './components/RcProSidebar';
import { isPanelVisible } from './components/RcProSidebar/utils';
import TopbarActions from './components/TopbarActions';
import locales from './locales';

interface PreviewProps {
  quote: Quote;
  client: Client;
  companyLogoUrl?: string | null;
  invoicingSpecialMentions: InvoicingSpecialMentions;
}

const SENT_ITEM_STATUSES: ReadonlyArray<string> = [
  QuoteStatus.Sent,
  QuoteStatus.Accepted,
];

const Preview: FC<PreviewProps> = ({
  client,
  companyLogoUrl = null,
  invoicingSpecialMentions,
  quote,
}) => {
  const { showToast } = useToast();
  const { openModal } = useQuoteModals();
  const history = useHistory();
  const { search } = useLocation();
  const defaultLang = useCurrentLanguage();
  const { isFeatureAvailable } = useCurrentPricingPlan();

  const [lang, setLang] = useState(defaultLang);
  const [contactOptions, setContactOptions] = useClientContacts();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const withSidebar = useMemo(
    () => isSidebarOpen && isPanelVisible(),
    [isSidebarOpen],
  );

  const colorScheme = useMemo(
    () => getColorScheme({ status: quote.status }),
    [quote.status],
  );

  const { loading: sendDummyLoading, sendDummyQuoteToUser } =
    useSendDummyQuoteToUser();

  const openResendWithShineModal = useCallback(() => {
    openModal({
      name: 'resend-with-shine',
      props: {
        clientId: client.clientId,
        quoteId: quote.quoteId,
      },
    });
  }, [client.clientId, openModal, quote.quoteId]);

  useEffect(() => {
    if (
      queryString.parse(search)?.resend &&
      SENT_ITEM_STATUSES.includes(quote.status)
    ) {
      openResendWithShineModal();
    }
  }, [openResendWithShineModal, quote.status, search]);

  const onSendDummy = async () => {
    logEvent({
      name: 'Send Dummy Quote To User',
      properties: { quoteId: quote.quoteId },
    });

    try {
      await sendDummyQuoteToUser({ input: { quoteId: quote.quoteId } });
      showToast({
        message: locales.sendDummy.success,
        type: 'success',
      });
    } catch {
      showToast({
        message: locales.sendDummy.error,
        type: 'error',
      });
    }
  };

  const { particularMentions, paymentMentions } = computeQuoteSpecialMentions(
    {
      client,
      company: quote.company,
      lateFeePercentage: quote.lateFeePercentage,
      lateMaxDays: quote.lateMaxDays,
      startAt: quote.startAt,
      tasks: quote.tasks,
    },
    invoicingSpecialMentions,
  );

  return (
    <>
      <PreviewLayout withSidebar={withSidebar}>
        <PreviewLayout.Topbar>
          <Flex
            alignItems="center"
            height="100%"
            justifyContent="space-between"
            paddingX="space-24"
          >
            <Flex alignItems="center">
              <NavigationButton
                onClick={() => history.replace(routes.quotes)}
                variant="close"
              />
              <Text
                fontWeight="weight-500"
                marginLeft="space-16"
                variant="primary"
              >
                {quote.fiscalId
                  ? locales.formatString(
                      locales.title.withFiscalId,
                      quote.fiscalId,
                    )
                  : locales.title.inProgress}
              </Text>
            </Flex>

            <HStack spacing="space-32">
              <TopbarActions
                clientId={client.clientId}
                clientName={client.name}
                lang={lang}
                openResendWithShineModal={openResendWithShineModal}
                quote={quote}
                setLang={setLang}
              />
            </HStack>
          </Flex>
        </PreviewLayout.Topbar>

        <PreviewLayout.Body colorScheme={colorScheme}>
          {quote.client && quote.company ? (
            <Flex flexDirection="column" height="100%" justifyContent="stretch">
              {quote.status === QuoteStatus.Pending ? (
                <ContactPicker
                  clientId={quote.clientId}
                  contactOptions={contactOptions}
                  loading={sendDummyLoading}
                  onChangeContactOptions={setContactOptions}
                  onSendDummy={onSendDummy}
                />
              ) : (
                <DetailHeader quote={quote} />
              )}

              {quote.status === QuoteStatus.Pending || !quote.pdf ? (
                <Box paddingBottom="space-52">
                  <Page boxShadow="shadow-small-2">
                    <QuoteRenderer
                      lang={lang}
                      quote={{
                        ...quote,
                        client: quote.client,
                        company: {
                          ...quote.company,
                          tradeName: quote.settings.displayTradeName
                            ? quote.company.tradeName
                            : '',
                        },
                        logoUrl: isFeatureAvailable(Feature.PersonalizedLogo)
                          ? companyLogoUrl
                          : '',
                        particularMentions,
                        paymentMentions,
                        settings: {
                          ...quote.settings,
                          quoteValidityDays:
                            quote.settings.quoteValidityDays ??
                            QUOTE_VALIDITY_DAYS_UNLIMITED,
                        },
                      }}
                    />
                  </Page>
                </Box>
              ) : (
                <Box flex={1}>
                  <PdfPreview
                    fileSrc={
                      lang === 'fr'
                        ? `${quote.pdf.frUrl}#navpanes=0&view=fitH`
                        : `${quote.pdf.enUrl}#navpanes=0&view=fitH`
                    }
                  />
                </Box>
              )}
            </Flex>
          ) : null}
        </PreviewLayout.Body>

        {withSidebar ? (
          <PreviewLayout.Sidebar>
            <RcProSidebar onClose={() => setIsSidebarOpen(false)} />
          </PreviewLayout.Sidebar>
        ) : null}
      </PreviewLayout>
    </>
  );
};

export default Preview;
